import React, { useState, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import FeedbackPanel, {
  FeedbackContext,
  FeedbackPayload,
} from "@/components/feedback/FeedbackPanel";
import { submitFeedback } from "@/lib/apiService";
import { MessageSquare, Loader2 } from "lucide-react";

interface FeedbackButtonProps {
  /** Custom button label */
  label?: string;
  /** Custom button variant */
  variant?: "default" | "outline" | "ghost" | "link" | "destructive" | "secondary";
  /** Custom button size */
  size?: "default" | "sm" | "lg" | "icon";
  /** Custom className for the button */
  className?: string;
  /** Default feedback context */
  defaultContext?: FeedbackContext;
  /** Whether to show icon */
  showIcon?: boolean;
  /** Optional client ID to associate feedback with */
  clientId?: string;
  /** Optional meal plan ID to associate feedback with */
  mealPlanId?: string;
  /** Custom title for the feedback panel */
  title?: string;
  /** Custom description for the feedback panel */
  description?: string;
  /** Custom title placeholder */
  titlePlaceholder?: string;
  /** Custom details placeholder */
  detailsPlaceholder?: string;
  /** Custom submit label */
  submitLabel?: string;
  /** Custom success toast description */
  successDescription?: string;
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  label = "Feedback",
  variant = "outline",
  size = "default",
  className,
  defaultContext = FeedbackContext.OVERALL_APPLICATION,
  showIcon = true,
  clientId: clientIdProp,
  mealPlanId: mealPlanIdProp,
  title = "Share feedback",
  description = "Please provide feedback on the overall usability of the application",
  titlePlaceholder = "Short summary (e.g., Draft meal plan issues)",
  detailsPlaceholder = "Please provide feedback on the overall usability of the application",
  submitLabel = "Send Feedback",
}) => {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Don't render on the feedback page or recipes list page
  if (
    location.pathname === "/feedback" ||
    location.pathname === "/recipes"
  ) {
    return null;
  }

  // Automatically extract clientId and mealPlanId from URL
  const { clientId, mealPlanId } = useMemo(() => {
    let detectedClientId: string | undefined = clientIdProp;
    let detectedMealPlanId: string | undefined = mealPlanIdProp;

    // If props are provided, use them (explicit override)
    if (clientIdProp) {
      detectedClientId = clientIdProp;
    } else {
      // Extract clientId from URL params
      // Routes: /clients/:id, /clients/:clientId/details-v2/meal-plan, /manual-meal-plan/:draftId/client/:clientId, /ai-meal-plan/draft/:draftId/client/:clientId, etc.
      detectedClientId = params.clientId || params.id || undefined;
    }

    if (mealPlanIdProp) {
      detectedMealPlanId = mealPlanIdProp;
    } else {
      // Extract mealPlanId from URL params or query
      // Routes:
      // - /finalized-meal-plan/:planId/client/:clientId -> planId
      // - /ai-meal-plan/draft/:draftId/client/:clientId -> draftId
      // - /automated-meal-plan/draft/:draftId/client/:clientId -> draftId
      // - /manual-meal-plan/:draftId/client/:clientId -> draftId
      // - Legacy fallback: /meal-plans/display/:clientId?draftId=... -> draftId (query)
      // - /meal-plans/:planId/clients/:clientId/nutrition/:dayNumber -> planId
      detectedMealPlanId = 
        params.planId || 
        params.draftId || 
        searchParams.get('draftId') || 
        undefined;
    }

    return {
      clientId: detectedClientId,
      mealPlanId: detectedMealPlanId,
    };
  }, [clientIdProp, mealPlanIdProp, params, searchParams]);

  const handleSubmit = async (payload: FeedbackPayload) => {
    const numericRating = payload.rating ? Number(payload.rating) : undefined;

    setIsSubmitting(true);
    try {
      await submitFeedback({
        feedbackType: payload.context,
        clientId,
        mealPlanId,
        title: payload.title,
        feedbackText: payload.details,
        rating: Number.isFinite(numericRating || NaN) ? numericRating : undefined,
        feedbackDate: new Date().toISOString().split("T")[0],
      });

      // Close the dialog after successful submission
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to submit feedback", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Automatically apply emerald styling for "Testing Feedback" buttons
  const isTestingFeedback = label === "Testing Feedback";
  const buttonClassName = isTestingFeedback
    ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-all duration-200"
    : className;
  const buttonVariant = isTestingFeedback ? "outline" : variant;

  return (
    <>
      <Button
        variant={buttonVariant}
        size={size}
        className={buttonClassName}
        onClick={() => setIsOpen(true)}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            {showIcon && <MessageSquare className="h-4 w-4 mr-2" />}
            {label}
          </>
        )}
      </Button>

      <Dialog open={isOpen} onOpenChange={(open) => !isSubmitting && setIsOpen(open)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Share Feedback</DialogTitle>
          </DialogHeader>
          <FeedbackPanel
            title={title}
            description={description}
            titlePlaceholder={titlePlaceholder}
            detailsPlaceholder={detailsPlaceholder}
            submitLabel={submitLabel}
            defaultContext={defaultContext}
            variant="inline"
            onSubmit={handleSubmit}
            hideHeader
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
